import React, { useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { isEqual, isEmpty } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { useLayout, DEFAULT_MODEL_TAB, DEFAULT_INSIGHTS_TAB, isModelTabPath } from "hoc/Layout/LayoutProvider";
import { APP_VERSION } from "utils/constants/version";

import Dialog, { CloseDialog } from "components/Dialog";
import Button from "components/Button";

import classes from "./style.module.scss";

import logo from "assets/images/logo.svg";
import CreateSquare from "assets/images/icons/JSX/create-square";
import Houses from "assets/images/icons/JSX/HousesIcon";
import ParametricIcon from "assets/images/icons/JSX/ParametricIcon";
import CodeLib from "assets/images/icons/JSX/CodeLibAlt";
import Upgrades from "assets/images/icons/JSX/Upgrades";
import GenerateIcon from "assets/images/icons/Icon-Generate-Report.svg?react";
import ArrowLeft from "assets/images/icons/arrow-left.svg";
import LogoWhite from "assets/images/volta-logo-white.svg";
import Star from "assets/images/icons/JSX/Star";
import CommunitiesIcon from "assets/images/icons/JSX/CommunitiesIcon";
import InsightsIcon from "assets/images/icons/JSX/Insights";

export const UnSavedChanges = ({ open, toggleDialog, onLeave }) => (
    <Dialog
        open={open}
        classes={{
            paperClass: classes.dialogPaper,
        }}
    >
        <CloseDialog closeActions={() => toggleDialog(false)} />
        <h2>You have unsaved changes</h2>
        <p>Any unsaved changes will be lost.</p>
        <div className={classes.buttons}>
            <Button
                onClick={() => {
                    onLeave();
                    toggleDialog(false);
                }}
                type="hollow"
            >
                Leave without Saving
            </Button>
            <Button onClick={() => toggleDialog(false)}>Stay on this Page</Button>
        </div>
    </Dialog>
);

const Navigation = ({ isCollapsed, isCollapsedDone, setIsCollapsed, setIsCollapsedDone }) => {
    const history = useHistory();
    const location = useLocation();
    const { modelTabActive, insightsTabActive } = useLayout();

    const modelTabPath = isModelTabPath(modelTabActive) ? modelTabActive : DEFAULT_MODEL_TAB;
    const insightsTabPath = insightsTabActive || DEFAULT_INSIGHTS_TAB;

    const [systemsDialog, toggleSystems] = useState(false);
    const [unsavedDialog, toggleUnsavedDialog] = useState(false);
    const [eventTarget, changeEventTarget] = useState("");

    const {
        modelDetails = {},
        modelData = {},
        modelDataToCompare,
        modelDetailsToCompare,
        modelTakeoffToCompare,
        modelTakeoff,
        linkedBaselineToCompare,
        linkedBaseline,
        dataEntryMode = "",
        dataEntryModeToCompare = "",
        modelId = null,
    } = useStateSelector("model") || {};
    const { community = {}, communityToCompare = {} } = useStateSelector("community") || {};
    const { packages: upgradePackages = {}, initPackages: initUpgradePackages = {} } =
        useStateSelector("upgrades") || {};
    const { packages: commUpgradePackages = {}, initPackages: initCommUpgradePackages = {} } =
        useStateSelector("commUpgrades") || {};
    const {
        userPermissions: {
            ENVIROCENTRE_REPORT = false,
            CBAT_INPUT = false,
            CHBA_NZ_ADMIN = false,
            VOLTA_DEV = false,
            COMMUNITY_ACCESS = false,
        } = {},
        isUserVerified = true,
        modelDir: { models = {} } = {},
    } = useStateSelector("user");
    const { isDirty: upgradeLibChanged } = useStateSelector("upgradeLibrary");

    const hasFiveModels = isUserVerified ? false : !isEmpty(models) ? Object.keys(models).length >= 5 : false;

    const dataChanged =
        !!modelData &&
        !(isEmpty(modelData) && isEmpty(modelDataToCompare)) &&
        (!isEqual(modelData, modelDataToCompare) ||
            !isEqual({ ...modelDetailsToCompare, lastEdited: {} }, { ...modelDetails, lastEdited: {} }) ||
            !isEqual(upgradePackages, initUpgradePackages) ||
            !isEqual(modelTakeoffToCompare, modelTakeoff) ||
            !isEqual(linkedBaselineToCompare, linkedBaseline) ||
            (dataEntryMode ?? "").toLowerCase() !== (dataEntryModeToCompare ?? "").toLowerCase());
    const commDataChanged =
        !!community &&
        (!isEqual(commUpgradePackages, initCommUpgradePackages) || !isEqual(community, communityToCompare));
    const checkModelChanges = useCallback(
        (event, href) => {
            if (
                (dataChanged && window.location.pathname.includes("/models/")) ||
                (dataChanged && window.location.pathname.includes("/insights/")) ||
                (upgradeLibChanged && window.location.pathname.includes("/upgrade-library")) ||
                (commDataChanged && window.location.pathname.includes("/communities/"))
            ) {
                event.preventDefault();
                changeEventTarget(href);
                toggleUnsavedDialog(true);
            } else {
                history.push("/dashboard");
            }
        },
        [dataChanged, commDataChanged, upgradeLibChanged]
    );

    const handleCollapsed = (e) => {
        e.preventDefault();

        setIsCollapsed(!isCollapsed);

        if (isCollapsed) {
            setIsCollapsedDone(false);
        }

        if (!isCollapsed) {
            setTimeout(() => {
                setIsCollapsedDone(true);
            }, 150);
        }
    };

    return (
        <div
            className={classNames(classes.sideBar, {
                [classes.sideBarCollapsed]: isCollapsed,
                [classes.sideBarCollapsedDone]: isCollapsedDone,
            })}
        >
            <div className={classes.logoWrapper} onClick={(event) => checkModelChanges(event, "/dashboard")}>
                {!isCollapsed ? (
                    <img src={logo} alt="Volta Snap Logo" />
                ) : (
                    <img className={classes.logoWhite} src={LogoWhite} alt="Volta Snap Logo" />
                )}
            </div>
            <ul className={classes.mainNav}>
                <li
                    className={classNames(classes.create, {
                        [classes.listItemCollapsed]: isCollapsedDone,
                        [classes.disabledNavLink]: hasFiveModels,
                    })}
                >
                    <NavLink
                        onClick={(event) => checkModelChanges(event, "/create")}
                        activeClassName={classes.active}
                        exact
                        to="/create"
                        style={{
                            pointerEvents: !hasFiveModels ? "auto" : "none",
                            opacity: !hasFiveModels ? 1 : 0.5,
                        }}
                    >
                        <CreateSquare /> {!isCollapsedDone ? "Create New Model" : ""}
                    </NavLink>
                </li>
                <li className={classNames({ [classes.listItemCollapsed]: isCollapsedDone })}>
                    {location.pathname.includes("/models/") || location.pathname.includes("/insights/") ? (
                        <div style={{ background: "rgba(98, 188, 248, 0.2)", padding: "6px", borderRadius: "8px" }}>
                            <NavLink
                                className={classNames({
                                    [classes.activeNewModel]: window.location.pathname.includes("models"),
                                })}
                                activeClassName={classes.active}
                                to={
                                    location.pathname.includes("/insights/")
                                        ? `/models/${modelId}${modelTabPath}`
                                        : "/dashboard"
                                }
                                onClick={
                                    location.pathname.includes("/insights/")
                                        ? undefined
                                        : (event) => checkModelChanges(event, "/dashboard")
                                }
                                style={{ padding: "6px" }}
                            >
                                <Houses /> <p style={{ padding: "0" }}>{!isCollapsedDone && "House Models"}</p>
                            </NavLink>
                            <div className={classNames({ [classes.disabledNavLink]: !isUserVerified || !modelId })}>
                                <NavLink
                                    className={classNames({
                                        [classes.activeInsights]: window.location.pathname.includes("insights"),
                                    })}
                                    activeClassName={classes.active}
                                    to={`/insights/${modelId}${insightsTabPath}`}
                                    style={{
                                        padding: "6px",
                                        marginTop: "5px",
                                        pointerEvents: isUserVerified && modelId ? "auto" : "none",
                                        opacity: isUserVerified && modelId ? 1 : 0.5,
                                    }}
                                >
                                    <InsightsIcon /> <p style={{ padding: "0" }}>{!isCollapsedDone && "Insights"}</p>
                                </NavLink>
                            </div>
                        </div>
                    ) : (
                        <NavLink
                            className={classNames({ [classes.active]: window.location.pathname.includes("models") })}
                            onClick={(event) => checkModelChanges(event, "/dashboard")}
                            activeClassName={classes.active}
                            to="/dashboard"
                        >
                            <Houses /> <p style={{ padding: "0" }}>{!isCollapsedDone && "House Models"}</p>
                        </NavLink>
                    )}
                </li>
                <li className={classNames({ [classes.listItemCollapsed]: isCollapsedDone })}>
                    <NavLink
                        onClick={(event) => checkModelChanges(event, "/code-library")}
                        activeClassName={classes.active}
                        to="/code-library"
                    >
                        <CodeLib /> {!isCollapsedDone ? "Code Library" : ""}
                    </NavLink>
                </li>
                <li className={classNames({ [classes.listItemCollapsed]: isCollapsedDone })}>
                    <NavLink
                        onClick={(event) => checkModelChanges(event, "/upgrade-library")}
                        activeClassName={classes.active}
                        to="/upgrade-library"
                    >
                        <Upgrades /> {!isCollapsedDone ? "Upgrade Library" : ""}
                    </NavLink>
                </li>
                {ENVIROCENTRE_REPORT && isUserVerified && (
                    <li className={classNames(classes.generate, { [classes.listItemCollapsed]: isCollapsedDone })}>
                        <NavLink
                            activeClassName={classes.active}
                            onClick={(event) => checkModelChanges(event, "/generate-report")}
                            to="/generate-report"
                        >
                            <GenerateIcon /> {!isCollapsedDone ? "Report Generator" : ""}
                        </NavLink>
                    </li>
                )}
                {isUserVerified && (
                    <li className={classes.generate}>
                        <NavLink
                            className={classNames({
                                [classes.active]:
                                    window.location.pathname.includes("/parametric-analysis/") ||
                                    window.location.pathname.includes("/analysis"),
                            })}
                            activeClassName={classes.activeParametric}
                            onClick={(event) => checkModelChanges(event, "/parametric-analysis/dashboard")}
                            to="/parametric-analysis/dashboard"
                        >
                            <ParametricIcon /> {!isCollapsedDone ? "Parametric Analysis" : ""}
                        </NavLink>
                    </li>
                )}
                {CBAT_INPUT && isUserVerified && (
                    <li className={classes.generate}>
                        <NavLink
                            activeClassName={classes.activeParametric}
                            onClick={(event) => checkModelChanges(event, "/legacy-parametric-analysis")}
                            to="/legacy-parametric-analysis"
                        >
                            <ParametricIcon /> {!isCollapsedDone ? "CBAT-Tableau Analysis" : ""}
                        </NavLink>
                    </li>
                )}
                {(CHBA_NZ_ADMIN || VOLTA_DEV || COMMUNITY_ACCESS) && isUserVerified && (
                    <li className={classNames({ [classes.listItemCollapsed]: isCollapsedDone })}>
                        <NavLink
                            className={`${window.location.pathname.includes("/communities/") && classes.active}`}
                            activeClassName={classes.active}
                            onClick={(event) => checkModelChanges(event, "/communities/dashboard")}
                            to="/communities/dashboard"
                        >
                            <CommunitiesIcon /> {!isCollapsedDone ? "Communities" : ""}
                        </NavLink>
                    </li>
                )}
            </ul>
            <UnSavedChanges
                open={unsavedDialog}
                toggleDialog={toggleUnsavedDialog}
                onLeave={() => history.push(eventTarget)}
            />
            <div className={classes.collapse}>
                {!isCollapsedDone && <div className={classes.versionText}>v{APP_VERSION}</div>}

                <div
                    // large
                    className={classNames(classes.collapseButton, { [classes.rotated]: isCollapsed })}
                    type="slate"
                    onClick={(e) => handleCollapsed(e)}
                >
                    <img src={ArrowLeft} />
                </div>
            </div>
        </div>
    );
};

export default Navigation;
